import "@/App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { Toaster } from "@/components/ui/sonner";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Packs from "@/pages/Packs";
import FAQ from "@/pages/FAQ";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";
import Login from "@/pages/Login";
import Espaces from "@/pages/Espaces";
import Team from "@/pages/Team";
import SeoPage from "@/pages/SeoPage";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Booking from "@/pages/Booking";
import Avis from "@/pages/Avis";
import Verified from "@/pages/Verified";
import Preregistration from "@/pages/Preregistration";
import WteLive, { WteLiveItem } from "@/pages/WteLive";
import Register from "@/pages/Register";
import DashboardLayout from "@/pages/dashboard/DashboardLayout";
import DashboardHome from "@/pages/dashboard/DashboardHome";
import Documents from "@/pages/dashboard/Documents";
import Suivi from "@/pages/dashboard/Suivi";
import PaymentReturn from "@/pages/dashboard/PaymentReturn";
import Facturation from "@/pages/dashboard/Facturation";
import Messagerie from "@/pages/dashboard/Messagerie";
import RendezVous from "@/pages/dashboard/RendezVous";
import Parrainage from "@/pages/dashboard/Parrainage";
import Profil from "@/pages/dashboard/Profil";
import AdminDashboard from "@/pages/admin/AdminDashboard";

function Protected({ children, staff }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-navy">Chargement…</div>;
  if (!user) return <Navigate to="/connexion" replace />;
  if (staff && !["admin", "staff"].includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  const [toastPos, setToastPos] = useState(() => (typeof window !== "undefined" && window.innerWidth < 768 ? "bottom-center" : "top-right"));
  useEffect(() => {
    const onResize = () => setToastPos(window.innerWidth < 768 ? "bottom-center" : "top-right");
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  useEffect(() => {
    try {
      let sid = localStorage.getItem("wte_visitor_id");
      if (!sid) { sid = (window.crypto?.randomUUID?.() || String(Date.now() + Math.random())); localStorage.setItem("wte_visitor_id", sid); }
      const base = process.env.REACT_APP_BACKEND_URL;
      fetch(`${base}/api/track/visit`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid, path: window.location.pathname, language: navigator.language }),
      }).catch(() => {});
    } catch (e) { /* noop */ }
  }, []);
  return (
    <div className="App">
      <AuthProvider>
        <LanguageProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Layout />}>
              <Route index element={<Home />} />
              <Route path="a-propos" element={<About />} />
              <Route path="notre-equipe" element={<Team />} />
              <Route path="nos-packs" element={<Packs />} />
              <Route path="faq" element={<FAQ />} />
              <Route path="contact" element={<Contact />} />
              <Route path="mentions-legales" element={<Legal type="mentions" />} />
              <Route path="confidentialite" element={<Legal type="confidentialite" />} />
              <Route path="cgv" element={<Legal type="cgv" />} />
              <Route path="blog" element={<Blog />} />
              <Route path="blog/:slug" element={<BlogPost />} />
              <Route path="rendez-vous" element={<Booking />} />
              <Route path="avis" element={<Avis />} />
              <Route path="entreprise-verifiee" element={<Verified />} />
              <Route path="preinscription-canada-2027" element={<Preregistration />} />
              <Route path="wte-live" element={<WteLive />} />
              <Route path="wte-live/:slug" element={<WteLiveItem />} />
              <Route path=":slug" element={<SeoPage />} />
            </Route>
            <Route path="/acces" element={<Espaces />} />
            <Route path="/connexion" element={<Login />} />
            <Route path="/inscription" element={<Register />} />
            <Route path="/dashboard" element={<Protected><DashboardLayout /></Protected>}>
              <Route index element={<DashboardHome />} />
              <Route path="documents" element={<Documents />} />
              <Route path="factures" element={<Facturation />} />
              <Route path="messagerie" element={<Messagerie />} />
              <Route path="rendez-vous" element={<RendezVous />} />
              <Route path="parrainage" element={<Parrainage />} />
              <Route path="profil" element={<Profil />} />
              <Route path="suivi" element={<Suivi />} />
              <Route path="paiement" element={<PaymentReturn />} />
            </Route>
            <Route path="/admin" element={<Protected staff><AdminDashboard /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position={toastPos} richColors />
          <WhatsAppButton />
        </BrowserRouter>
        </LanguageProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
